/**
 * Persist and read resolved PostGrid packet attachments on generated letters / package snapshots.
 */

import {
  attachmentIsFullPage,
  buildEnvelopePrintableAttachments,
  buildPdfApiAttachments,
  dedupeResolvedAttachments,
  getAttachmentDisplayLabel,
  getAttachmentDisplayUrl,
  isDataBreachLetter,
  normalizeDataBreachProof,
  resolveAttachmentRenderMode,
  resolveEffectiveDataBreachProof,
} from './disputeEnvelopeAttachments';

export const RESOLVED_PACKET_FIELD = 'resolved_packet_attachments';
export const LEGACY_PACKET_FIELD = 'final_packet_attachments';

export function normalizePersistedPacketAttachment(
  att,
  { includedInPostgrid = false, resolvedAt } = {}
) {
  if (!att) return null;
  const type = String(att.type || att.category || att.document_type || '').toLowerCase();
  const url = getAttachmentDisplayUrl(att) || att.url || att.file_url || '';
  const label = att.label || getAttachmentDisplayLabel({ ...att, type });

  return {
    id: att.document_id || att.id || att.file_id || undefined,
    document_id: att.document_id || att.id || undefined,
    file_id: att.file_id || undefined,
    type,
    category: type,
    label,
    filename: att.filename || att.name || att.title || undefined,
    name: att.name || att.title || att.filename || undefined,
    url: url || undefined,
    file_url: att.file_url || url || undefined,
    source: att.source || undefined,
    included_in_postgrid: Boolean(
      att.included_in_postgrid ?? att.includedInPostgrid ?? includedInPostgrid
    ),
    resolved_at: att.resolved_at || att.resolvedAt || resolvedAt || new Date().toISOString(),
  };
}

export function normalizePersistedPacketList(attachments, options = {}) {
  const resolvedAt = options.resolvedAt || new Date().toISOString();
  return dedupeResolvedAttachments(attachments || [])
    .map((att) => normalizePersistedPacketAttachment(att, { ...options, resolvedAt }))
    .filter(Boolean);
}

export function filterPacketAttachmentsForLetter(attachments, letter) {
  if (isDataBreachLetter(letter)) return attachments || [];
  return (attachments || []).filter(
    (att) => String(att.type || att.category || '').toLowerCase() !== 'data_breach_proof'
  );
}

export function getPersistedPacketAttachmentsFromRecord(record) {
  if (!record) return null;
  const list =
    record[RESOLVED_PACKET_FIELD] ||
    record[LEGACY_PACKET_FIELD] ||
    record.metadata?.[RESOLVED_PACKET_FIELD];
  if (!Array.isArray(list) || !list.length) return null;
  return list;
}

export function printableFromPersistedPacket(records) {
  if (!Array.isArray(records) || !records.length) return [];
  return records.map((r) => {
    const url = r.url || r.file_url;
    return {
      ...r,
      type: r.type || r.category,
      category: r.category || r.type,
      title: r.label || r.title,
      label: r.label || r.title,
      server_url: url,
      file_url: r.file_url || url,
      cropped_url: url,
      preview_url: url,
      attached: true,
      included: true,
    };
  });
}

export function pdfApiFromPersistedPacket(records) {
  return printableFromPersistedPacket(records).map((att) => ({
    type: att.type,
    category: att.category || att.type,
    document_id: att.document_id || att.id || undefined,
    filename: att.filename || att.name || att.label,
    title: att.title || att.label,
    source: att.source,
    server_url: att.server_url || att.url || att.file_url,
    file_url: att.file_url || att.url,
    cropped_url: att.cropped_url || att.url || att.file_url,
    preview_url: att.preview_url || att.url || att.file_url,
    is_server_saved: Boolean(att.is_server_saved || att.source === 'system_default'),
    is_full_page: attachmentIsFullPage(att),
    full_page: attachmentIsFullPage(att),
    render_mode: att.render_mode || resolveAttachmentRenderMode(att),
    page_break_before: Boolean(att.page_break_before),
    included_in_postgrid: Boolean(att.included_in_postgrid),
    crop_metadata: att.crop_metadata || undefined,
    mime_type: att.mime_type || undefined,
  }));
}

/** Build resolved printable + API attachments from wizard envelope context. */
export function buildWizardPacketAttachments({
  letter,
  packageIdentitySupport = {},
  dataBreachProof = null,
  perLetterAttachments = {},
  packageSharedAttachments = [],
  ssnApprovals = {},
  letterId,
  includedInPostgrid = false,
}) {
  const target = letter || {};
  const lid =
    letterId ||
    target.letter_id ||
    target.id ||
    `letter_${String(target.bureau || 'unknown').toLowerCase()}`;

  const isDataBreach = isDataBreachLetter(target);
  let effectiveBreachProof = dataBreachProof;
  if (isDataBreach) {
    effectiveBreachProof = resolveEffectiveDataBreachProof(
      dataBreachProof || normalizeDataBreachProof(null)
    );
  }

  const envelopeInputs = {
    letter: target,
    packageIdentitySupport,
    dataBreachProof: isDataBreach ? effectiveBreachProof : null,
    perLetterAttachments,
    packageSharedAttachments,
    letterId: lid,
    ssnApprovals,
  };

  const printable = filterPacketAttachmentsForLetter(
    dedupeResolvedAttachments(buildEnvelopePrintableAttachments(envelopeInputs)),
    target
  );
  const pdfApi = filterPacketAttachmentsForLetter(
    buildPdfApiAttachments(envelopeInputs),
    target
  );
  const resolvedAt = new Date().toISOString();
  const persisted = normalizePersistedPacketList(printable, {
    includedInPostgrid,
    resolvedAt,
  });

  return {
    resolvedAttachments: printable,
    pdfApiAttachments: pdfApi,
    persistedPacketAttachments: persisted,
    letterId: lid,
    isDataBreach,
  };
}

export async function persistResolvedPacketAttachments(metro2Service, letterId, payload, token) {
  if (!metro2Service || !letterId || !payload || !Array.isArray(payload.attachments)) return null;
  try {
    return await metro2Service.putResolvedPacketAttachments(letterId, payload, token);
  } catch (error) {
    console.warn('[packet attachments] persist failed', letterId, error);
    return null;
  }
}

/** Persist packet attachments for every letter in a wizard package. */
export async function syncWizardPackagePacketAttachments({
  letters = [],
  packageIdentitySupport,
  dataBreachProof,
  perLetterAttachments,
  packageSharedAttachments,
  ssnApprovals,
  packageId,
  includedInPostgrid = false,
  metro2Service,
  token,
}) {
  const results = [];
  for (const letter of letters) {
    const letterId = letter?.letter_id || letter?.id;
    if (!letterId) continue;

    const packet = buildWizardPacketAttachments({
      letter,
      packageIdentitySupport,
      dataBreachProof,
      perLetterAttachments,
      packageSharedAttachments,
      ssnApprovals,
      letterId,
      includedInPostgrid,
    });

    const saved = await persistResolvedPacketAttachments(
      metro2Service,
      letterId,
      {
        attachments: packet.persistedPacketAttachments,
        package_id: packageId,
        included_in_postgrid: includedInPostgrid,
      },
      token
    );
    results.push({ letterId, count: packet.persistedPacketAttachments.length, saved });
  }
  return results;
}
